// src/components/Roadmap/RoadmapSection.js
import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Router,
  Cable,
  PhoneForwarded,
  DevicesOther,
  Cloud,
  TravelExplore,
} from "@mui/icons-material";

// Custom styled components for the roadmap cards
const RoadmapCard = styled(Card)(({ theme }) => ({
  height: "100%",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: theme.shadows[8],
  },
}));

const PhaseIndicator = styled(Box)(({ theme, color }) => ({
  backgroundColor: color || theme.palette.primary.main,
  color: "#fff",
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.shape.borderRadius,
  display: "inline-block",
  marginBottom: theme.spacing(1),
  fontWeight: "bold",
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: "50%",
  padding: theme.spacing(1.5),
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: theme.spacing(2),
  boxShadow: theme.shadows[2],
}));

const RoadmapSection = () => {
  const roadmapItems = [
    {
      phase: "Phase 1",
      title: "CPaaS Suite Launch",
      description:
        "Converging Bulk Messaging Gateway (BMG), Voice Communications Platform, and Messaging APIs under one SaaS umbrella for unified communication services.",
      icon: <Cloud fontSize="large" color="primary" />,
      color: "#3f51b5", // Indigo
      timeline: "2024-2025",
    },
    {
      phase: "Phase 2",
      title: "AI-Powered Voice Assistants",
      description:
        "Development of advanced voice assistants using GPT and neural Text-to-Speech technologies to enhance conversational capabilities.",
      icon: <PhoneForwarded fontSize="large" color="primary" />,
      color: "#2196f3", // Blue
      timeline: "2025-2026",
    },
    {
      phase: "Phase 3",
      title: "Network-Level Solutions",
      description:
        "Implementation of SMSC modernization, MNP lookups, and enhanced anti-spam enforcement capabilities for telecom operators.",
      icon: <Router fontSize="large" color="primary" />,
      color: "#009688", // Teal
      timeline: "2026-2027",
    },
    {
      phase: "Phase 4",
      title: "Device-Level Communication Agents",
      description:
        "Creation of intelligent device-level communication agents for smarter telecom applications and enhanced user experiences.",
      icon: <DevicesOther fontSize="large" color="primary" />,
      color: "#4caf50", // Green
      timeline: "2027-2028",
    },
    {
      phase: "Phase 5",
      title: "Next-Gen Protocol Expansion",
      description:
        "Expansion into RCS, 5G messaging, and IoT messaging protocols to build comprehensive future-ready communication ecosystems.",
      icon: <TravelExplore fontSize="large" color="primary" />,
      color: "#ff9800", // Orange
      timeline: "2028-2029",
    },
  ];

  return (
    <Box sx={{ py: 8, backgroundColor: "background.paper" }}>
      <Container maxWidth="lg">
        <Typography variant="h2" component="h2" align="center" gutterBottom>
          Technology Roadmap
        </Typography>
        <Typography
          variant="h5"
          color="text.secondary"
          align="center"
          paragraph
          sx={{ mb: 6 }}
        >
          Empowering the future of telecom communication through strategic
          innovation
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", mb: 6 }}>
          <Cable fontSize="large" color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" color="primary.main">
            Innovation Timeline: 2024-2029
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {roadmapItems.map((item, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <RoadmapCard elevation={3}>
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  <PhaseIndicator color={item.color}>
                    {item.phase} • {item.timeline}
                  </PhaseIndicator>

                  <IconWrapper>{item.icon}</IconWrapper>

                  <Typography
                    variant="h5"
                    component="h3"
                    gutterBottom
                    fontWeight="bold"
                  >
                    {item.title}
                  </Typography>

                  <Typography variant="body1">{item.description}</Typography>
                </CardContent>
              </RoadmapCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default RoadmapSection;
